/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Form, Input, Row, Select, Space, Typography } from 'antd';

// Misc imports
import { LANGUAGE_OPTIONS } from '../../constants/main';
import { generateNlpFlowGraph } from '../../utils/misc/generateFlowGraph';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useCreateNlpFlowMutation } from '../../store/nlpFlows/nlpFlowsApi';

// Constant declarations
const { Text } = Typography;

const NlpFlowForm = ({
  onSuccess
}) => {
  const [form] = Form.useForm();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { primaryBotLanguage, botLanguages } = useBotLanguage();

  const [createNlpFlow, { isLoading: isCreatingNlpFlow }] = useCreateNlpFlowMutation({ fixedCacheKey: 'create-nlp-flow-shared' });

  useEffect(() => {
    // Reset data on unmount
    return () => form.resetFields();
  }, []);

  const onSubmit = async (values) => {
    const valuesToSubmit = generateNlpFlowGraph(values.name, values.language);

    const createdFlow = await createNlpFlow({ orgId, tenantId, data: valuesToSubmit }).unwrap();

    if (createdFlow) {
      // Trigger callback to notify parent component of success
      onSuccess(createdFlow);
      form.resetFields();
    }
  };

  return (
    <Row gutter={24}>
      <Col xs={24}>
        <Form
          name="nlpFlowCreateForm"
          layout="vertical"
          onFinish={onSubmit}
          form={form}
        >
          <Row gutter={16} className="mb-3">
            <Col xs={24}>
              <Form.Item
                name="name"
                label={t('Name')}
                rules={[{
                  required: true,
                  whitespace: true,
                  message: `${t('Please type the name of the NLP Flow.')}`
                }]}
              >
                <Input
                  placeholder={t('Type the name of the NLP Flow')}
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item
                name="language"
                label={t('Language')}
                rules={[{
                  required: true,
                  message: `${t('Please choose a language.')}`
                }]}
              >
                <Select
                  placeholder={t('Choose a language')}
                  allowClear
                  size="large"
                  dropdownMatchSelectWidth={false}
                  showSearch
                  filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
                  options={botLanguages
                    .map(language => {
                      const currentLang = LANGUAGE_OPTIONS.find(lang => lang.key === language);
                      if(!currentLang) {
                        return {};
                      }
                      return {
                        label: <Space size={4}>{currentLang?.icon} {currentLang.label} {currentLang.key === primaryBotLanguage ? <Text type="secondary">{t('(primary)')}</Text> : null}</Space>,
                        value: currentLang.key,
                        searchValue: currentLang.label
                      };
                    })
                  }
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isCreatingNlpFlow}
            >
              {t('Create NLP Flow')}
            </Button>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

NlpFlowForm.propTypes = {
  onSuccess: PropTypes.func
};

export default NlpFlowForm;
